import { useCallback, useEffect, useState } from "react"

export type Appearance = "light" | "dark" | "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
  document.documentElement.style.colorScheme = isDark ? "dark" : "light"
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const handleSystemThemeChange = () => {
  const currentAppearance = localStorage.getItem("appearance") as Appearance
  applyTheme(currentAppearance ?? "system")
}

export function initializeTheme() {
  const savedAppearance =
    (localStorage.getItem("appearance") as Appearance) || "system"

  applyTheme(savedAppearance)

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

// IMP-292: force light mode for light-only pages (the public receptionist
// landing pages). initializeTheme() runs app-wide and adds `.dark` to <html>
// for OS-dark visitors, which leaks into portaled UI like the activate dialog
// (its `dark:` variants and inherited `foreground` colour both key off the
// `.dark` ancestor, so a CSS-variable swap can't fully undo them). Drop `.dark`
// while the page is mounted; restore the prior state on unmount.
export function useForceLightMode() {
  useEffect(() => {
    const el = document.documentElement
    const wasDark = el.classList.contains("dark")
    const prevColorScheme = el.style.colorScheme
    el.classList.remove("dark")
    el.style.colorScheme = "light"
    return () => {
      if (wasDark) el.classList.add("dark")
      el.style.colorScheme = prevColorScheme
    }
  }, [])
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(() => {
    if (typeof window === "undefined") return "system"
    const saved = localStorage.getItem("appearance") as Appearance | null
    return saved ?? "system"
  })

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    if (mode === "system") {
      localStorage.removeItem("appearance")
    } else {
      localStorage.setItem("appearance", mode)
    }
    applyTheme(mode)
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}
